import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import FormNavButtons from 'platform/forms-system/src/js/components/FormNavButtons';
import {
  VaRadio,
  VaModal,
  VaAlert,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { scrollToFirstError } from 'platform/utilities/scroll';
import {
  form0781HeadingTag,
  mentalHealthSupportAlert,
  titleWithTag,
} from './index';
import { checkValidations } from '../../shared/utils/validations';
import { track0781WorkflowChoiceSelected } from '../../shared/utils/tracking/form0781Tracking';
import {
  trackWorkflowChoiceDestructiveModalAction,
  trackWorkflowChoiceDestructiveModalRendered,
  trackWorkflowChoicePageRendered,
  trackWorkflowChoiceSuccessAlertRendered,
  WORKFLOW_CHOICE_MODAL_ACTIONS,
  WORKFLOW_CHOICE_MODAL_TRIGGER_TYPES,
  WORKFLOW_CHOICE_MODAL_TYPES,
} from '../utils/tracking/features/form0781WorkflowChoiceTracking';
import useDestructiveConfirmation from '../../shared/hooks/useDestructiveConfirmation';

import { form0781WorkflowChoices } from './workflowChoices';

export const workflowChoicePageTitle =
  'Optional statement if your claim includes mental health conditions';

export const form0781WorkflowChoiceDescription =
  'Do you want to add a statement to support mental health conditions related to traumatic events?';

export const form0781WorkflowChoiceLabels = Object.freeze({
  [form0781WorkflowChoices.COMPLETE_ONLINE_FORM]:
    'Yes, and I want to answer the questions online.',
  [form0781WorkflowChoices.SUBMIT_PAPER_FORM]:
    'Yes, and I want to fill out a PDF to upload.',
  [form0781WorkflowChoices.OPT_OUT_OF_FORM0781]:
    'No, I don’t want to add this statement to my claim.',
});

export const workflowChoicePageDescription = () => {
  return (
    <>
      <p>
        We encourage you to submit this optional statement if both of these
        descriptions are true for you:
      </p>
      <ul>
        <li>
          Your claim includes one or more mental health conditions,{' '}
          <strong>and</strong>
        </li>
        <li>
          The mental health conditions relate to a traumatic event that happened
          during your military service
        </li>
      </ul>
      <p>We’ll use the information you provide to support your claim.</p>
      <p>
        If these descriptions don’t apply to you, you can skip adding this
        statement.
      </p>
      <p>
        <strong>Here’s what to know before you decide:</strong>
      </p>
      <ul>
        <li>
          You can submit this statement in 1 of 2 ways: Answer questions online
          or upload a PDF version of the questions.
        </li>
        <li>
          We’ll ask you to describe the traumatic events you experienced and how
          they relate to your mental health conditions. You’ll likely need about
          45 minutes to answer all the questions.
        </li>
        <li>
          You can skip questions you can’t or don’t want to answer. And you can
          save your in-progress online form anytime if you need a break.
        </li>
        <li>
          We’ll ask you to share supporting evidence (like health records) later
          in the form.
        </li>
      </ul>
    </>
  );
};

export const traumaticEventsExamples = (
  <va-accordion open-single>
    <va-accordion-item class="vads-u-margin-y--3" id="first" bordered>
      <h3 slot="headline">
        Examples of mental health conditions and traumatic events
      </h3>
      <h4 className="vads-u-margin-top--0">
        Examples of mental health conditions
      </h4>
      <p>
        Some examples of diagnosed mental health conditions include, but are not
        limited to:
      </p>
      <ul>
        <li>Post traumatic stress disorder (PTSD)</li>
        <li>Depression</li>
        <li>Anxiety</li>
        <li>Bipolar disorder</li>
      </ul>
      <h4 className="vads-u-margin-top--0">
        Examples of traumatic events related to combat
      </h4>
      <ul>
        <li>You were engaged in combat with enemy forces</li>
        <li>You experienced fear of hostile military or terrorist activity</li>
        <li>You served in an imminent danger area</li>
        <li>You served as a drone aircraft crew member</li>
      </ul>
      <h4 className="vads-u-margin-top--0">
        Examples of traumatic events related to sexual assault or harassment
      </h4>
      <ul>
        <li>
          You experienced pressure to engage in sexual activities (for example,
          someone threatened you with bad treatment for refusing sex, or
          promised you better treatment in exchange for sex)
        </li>
        <li>
          You were pressured into sexual activities against your will (for
          example, when you were asleep or intoxicated)
        </li>
        <li>You were physically forced into sexual activities</li>
        <li>
          You experienced offensive comments about your body or sexual
          activities
        </li>
        <li>You experienced repeated unwanted sexual advances</li>
        <li>
          You experienced someone touching or grabbing you against your will,
          including during hazing
        </li>
      </ul>
      <h4 className="vads-u-margin-top--0">
        Examples of traumatic events related to other personal interactions
      </h4>
      <ul>
        <li>
          You experienced physical assault, battery, robbery, mugging, stalking,
          or harassment by a person who wasn’t part of an enemy force
        </li>
        <li>You experienced domestic intimate partner abuse or harassment</li>
      </ul>
      <h4 className="vads-u-margin-top--0">
        Examples of other traumatic events
      </h4>
      <ul>
        <li>You got into a car accident</li>
        <li>You witnessed a natural disaster, like a hurricane</li>
        <li>You worked on burn ward or graves registration</li>
        <li>
          You witnessed the death, injury, or threat to another person or to
          yourself, that was caused by something other than a hostile military
          or terrorist activity
        </li>
        <li>
          You experienced or witnessed friendly fire that occurred on a gunnery
          range during a training mission
        </li>
      </ul>
    </va-accordion-item>
  </va-accordion>
);

export const sectionsOfMentalHealthStatement = [
  'Traumatic events from your military service',
  'Behavioral changes',
  'Types of providers who treated  your traumatic events',
  'Types of documents about your mental health conditions',
];

export const mentalHealthKeys = [
  'treatmentReceivedVaProvider',
  'treatmentReceivedNonVaProvider',
  'treatmentNoneCheckbox',
  'supportingEvidenceWitness',
  'supportingEvidenceUnlisted',
  'supportingEvidenceReports',
  'supportingEvidenceRecords',
  'supportingEvidenceOther',
  'supportingEvidenceNoneCheckbox',
  'behaviorsDetails',
  'workBehaviors',
  'healthBehaviors',
  'otherBehaviors',
  'eventTypes',
  'form781Upload',
  'additionalInformation',
  'events',
];

const confirmationDataUpload = {
  yes: 'Yes, upload PDF instead',
  no: 'No, return to claim',
};

const confirmationDataOptOut = {
  yes: 'Yes, delete my statement',
  no: 'No, return to claim',
};

const confirmationCompleteOnline = {
  yes: 'Yes, answer online instead',
  no: 'No, return to claim',
};

const modalDescriptionUpload = (
  <>
    <p>
      If you choose to upload a PDF statement, we’ll delete this information
      from your claim:
    </p>
    <p>
      <ul>
        {sectionsOfMentalHealthStatement.map((section, i) => (
          <li key={i}>{section}</li>
        ))}
      </ul>
    </p>
  </>
);

const modalDescriptionSkip = (
  <>
    <p>
      If you choose to delete this statement, we’ll delete this information from
      your claim:
    </p>
    <ul>
      {sectionsOfMentalHealthStatement.map((section, i) => (
        <li key={i}>
          <b>{section}</b>
        </li>
      ))}
    </ul>
  </>
);

const modalDescriptionOnline = formData => {
  return (
    <>
      <p>
        If you choose to answer questions online, we’ll delete this PDF you
        uploaded:
      </p>
      <p>
        <ul>
          {formData.form781Upload.map((file, index) => (
            <li key={index}>
              <strong>{file.name}</strong>
            </li>
          ))}
        </ul>
      </p>
    </>
  );
};

const alertDescriptionSkip =
  'We deleted your statement about mental health conditions. You can still add a statement anytime before you submit your claim by choosing to answer questions online or uploading a PDF statement.';

const alertDescriptionUpload =
  'We’ve deleted information you entered online about mental health conditions.';
const alertDescriptionOnline = 'We’ve deleted your uploaded PDF.';

export const modalTitleSkip =
  'Delete your statement about mental health conditions?';
export const modalTitleUpload = 'Upload a PDF statement instead?';
export const modalTitleOnline = 'Answer questions online instead?';

/**
 * Remove all mental health statement fields and optionally set replacement values.
 *
 * @param {Object} data Current form data.
 * @param {(updatedData: Object) => void} setFormData Form state setter.
 * @param {Object} [extraFields] Optional fields to merge after deletion.
 */
const deleteMentalHealthStatement = (data, setFormData, extraFields) => {
  const updatedData = { ...data };

  mentalHealthKeys.forEach(key => {
    delete updatedData[key];
  });

  if (extraFields) {
    Object.assign(updatedData, extraFields);
  }

  setFormData(updatedData);
};

const deepCheck = value => {
  switch (typeof value) {
    case 'boolean':
      return value === true;
    case 'string':
      return value.trim() !== '';
    case 'number':
      return true;
    case 'object':
      if (value === null) return false;

      return Object.values(value).some(nestedValue => deepCheck(nestedValue));

    default:
      return false;
  }
};

const checkMentalHealthData = formData => {
  return mentalHealthKeys.some(key => {
    if (!(key in formData)) {
      return false;
    }

    return deepCheck(formData[key]);
  });
};

const getModalTypeForWorkflowChoice = workflowChoice => {
  switch (workflowChoice) {
    case form0781WorkflowChoices.COMPLETE_ONLINE_FORM:
      return WORKFLOW_CHOICE_MODAL_TYPES.DELETE_UPLOADED_PDF;
    case form0781WorkflowChoices.SUBMIT_PAPER_FORM:
    case form0781WorkflowChoices.OPT_OUT_OF_FORM0781:
      return WORKFLOW_CHOICE_MODAL_TYPES.DELETE_ONLINE_INFORMATION;
    default:
      return WORKFLOW_CHOICE_MODAL_TYPES.DELETE_ONLINE_INFORMATION;
  }
};

const WorkflowChoicePage = props => {
  const {
    data,
    goBack,
    goForward,
    setFormData,
    contentBeforeButtons,
    contentAfterButtons,
    onReviewPage,
    updatePage,
  } = props;

  const selectionField = 'view:selectedMentalHealthWorkflowChoice';
  const [previousWorkflowChoice, setPreviousWorkflowChoice] = useState(
    data?.['view:previousMentalHealthWorkflowChoice'] ?? null,
  );
  const [
    selectedMentalHealthWorkflowChoice,
    setSelectedMentalHealthWorkflowChoice,
  ] = useState(data?.mentalHealthWorkflowChoice ?? null);

  const [hasError, setHasError] = useState(null);
  const [shouldGoForward, setShouldGoForward] = useState(false);

  const hasDestructiveData = useCallback(
    () =>
      previousWorkflowChoice !== null &&
      previousWorkflowChoice !== selectedMentalHealthWorkflowChoice &&
      checkMentalHealthData(data),
    [previousWorkflowChoice, selectedMentalHealthWorkflowChoice, data],
  );

  const setPreviousData = useCallback(() => {
    const formData = {
      ...data,
      'view:previousMentalHealthWorkflowChoice':
        selectedMentalHealthWorkflowChoice,
      mentalHealthWorkflowChoice: selectedMentalHealthWorkflowChoice,
    };
    setPreviousWorkflowChoice(selectedMentalHealthWorkflowChoice);
    setFormData(formData);
    setShouldGoForward(true);
  }, [data, selectedMentalHealthWorkflowChoice, setFormData]);

  const {
    modalVisible,
    alertVisible,
    isSidenavTriggered,
    triggerIfNeeded,
    confirmAndDelete,
    cancelAndRevert,
    dismissAlert,
  } = useDestructiveConfirmation({
    hasDestructiveData,
    onConfirm: () => {
      deleteMentalHealthStatement(data, setFormData, {
        mentalHealthWorkflowChoice: selectedMentalHealthWorkflowChoice,
        'view:previousMentalHealthWorkflowChoice':
          selectedMentalHealthWorkflowChoice,
      });
      setPreviousWorkflowChoice(selectedMentalHealthWorkflowChoice);
    },
    onProceed: setPreviousData,
  });

  const selectedChoice = selectedMentalHealthWorkflowChoice ?? null;
  const destructiveModalType = getModalTypeForWorkflowChoice(selectedChoice);
  const destructiveModalTriggerType = isSidenavTriggered
    ? WORKFLOW_CHOICE_MODAL_TRIGGER_TYPES.SIDENAV
    : WORKFLOW_CHOICE_MODAL_TRIGGER_TYPES.CONTINUE;

  useEffect(() => {
    trackWorkflowChoicePageRendered();
  }, []);

  useEffect(() => {
    if (!modalVisible) {
      return;
    }

    trackWorkflowChoiceDestructiveModalRendered({
      modalType: destructiveModalType,
      triggerType: destructiveModalTriggerType,
    });
  }, [modalVisible, destructiveModalType, destructiveModalTriggerType]);

  useEffect(() => {
    if (!alertVisible) {
      return;
    }

    trackWorkflowChoiceSuccessAlertRendered({
      alertType: destructiveModalType,
      triggeredBySidenav: isSidenavTriggered,
    });
  }, [alertVisible, destructiveModalType, isSidenavTriggered]);

  useEffect(() => {
    if (
      shouldGoForward &&
      data?.mentalHealthWorkflowChoice === selectedMentalHealthWorkflowChoice
    ) {
      setShouldGoForward(false);
      goForward(data);
    }
  }, [data, goForward, selectedMentalHealthWorkflowChoice, shouldGoForward]);

  const missingSelectionErrorMessage =
    'A response is needed for this question.';

  const missingSelection = (error, _fieldData, formData) => {
    if (!formData?.[selectionField]) {
      error.addError?.(missingSelectionErrorMessage);
    }
  };

  const checkErrors = (formData = data) => {
    const error = checkValidations(
      [missingSelection],
      data?.['view:selectedMentalHealthWorkflowChoice'],
      formData,
    );

    const result = error?.[0] || null;
    setHasError(result);

    return result;
  };

  const { primaryText, secondaryText, modalContent, alertContent, modalTitle } =
    (() => {
      switch (selectedChoice) {
        case form0781WorkflowChoices.SUBMIT_PAPER_FORM:
          return {
            primaryText: confirmationDataUpload.yes,
            secondaryText: confirmationDataUpload.no,
            modalContent: modalDescriptionUpload,
            alertContent: alertDescriptionUpload,
            modalTitle: modalTitleUpload,
          };
        case form0781WorkflowChoices.OPT_OUT_OF_FORM0781:
          return {
            primaryText: confirmationDataOptOut.yes,
            secondaryText: confirmationDataOptOut.no,
            modalContent: modalDescriptionSkip,
            alertContent: alertDescriptionSkip,
            modalTitle: modalTitleSkip,
          };
        default:
          return {
            primaryText: confirmationCompleteOnline.yes,
            secondaryText: confirmationCompleteOnline.no,
            modalContent: data.form781Upload && modalDescriptionOnline(data),
            alertContent: alertDescriptionOnline,
            modalTitle: modalTitleOnline,
          };
      }
    })();

  const handlers = {
    onSelection: event => {
      const { value } = event?.detail || {};
      if (value) {
        track0781WorkflowChoiceSelected({ workflowChoice: value });
        setSelectedMentalHealthWorkflowChoice(value);
        setFormData({
          ...data,
          'view:selectedMentalHealthWorkflowChoice': value,
        });
      }
    },
    onSubmit: event => {
      event.preventDefault();
      if (checkErrors()) {
        scrollToFirstError({ focusOnAlertRole: true });
      } else {
        triggerIfNeeded();
      }
    },
    onUpdatePage: event => {
      event.preventDefault();
      if (checkErrors()) {
        scrollToFirstError({ focusOnAlertRole: true });
      } else if (hasDestructiveData()) {
        triggerIfNeeded();
      } else {
        dismissAlert();
        const formData = {
          ...data,
          'view:previousMentalHealthWorkflowChoice':
            selectedMentalHealthWorkflowChoice,
          mentalHealthWorkflowChoice: selectedMentalHealthWorkflowChoice,
        };
        setPreviousWorkflowChoice(selectedMentalHealthWorkflowChoice);
        setFormData(formData);
        setTimeout(() => {
          updatePage(event);
        }, 100);
      }
    },
    onCloseModal: () => {
      trackWorkflowChoiceDestructiveModalAction({
        modalType: destructiveModalType,
        triggerType: destructiveModalTriggerType,
        action: WORKFLOW_CHOICE_MODAL_ACTIONS.CANCEL,
      });
      cancelAndRevert();
    },
    onGoForward: () => {
      trackWorkflowChoiceDestructiveModalAction({
        modalType: destructiveModalType,
        triggerType: destructiveModalTriggerType,
        action: WORKFLOW_CHOICE_MODAL_ACTIONS.CONFIRM,
      });
      confirmAndDelete();
    },
    onGoBack: () => {
      goBack(data);
    },
    onCloseAlert: () => {
      dismissAlert();
    },
  };

  return (
    <form onSubmit={handlers.onSubmit}>
      <VaAlert
        id="success-alert"
        status="success"
        role="alert"
        closeable
        visible={alertVisible}
        onCloseEvent={handlers.onCloseAlert}
        class="vads-u-margin-bottom--4"
        uswds
      >
        {alertContent}
      </VaAlert>
      <fieldset className="vads-u-margin-bottom--2">
        <legend id="root__title" className="schemaform-block-title">
          {titleWithTag(workflowChoicePageTitle, form0781HeadingTag)}
        </legend>
        <div>
          {workflowChoicePageDescription()}
          <div>
            <VaRadio
              label={form0781WorkflowChoiceDescription}
              label-header-level="4"
              required
              uswds="true"
              class="rjsf-web-component-field hydrated"
              aria-invalid="false"
              onVaValueChange={handlers.onSelection}
              error={hasError}
            >
              <va-radio-option
                label={
                  form0781WorkflowChoiceLabels[
                    form0781WorkflowChoices.COMPLETE_ONLINE_FORM
                  ]
                }
                name="private"
                value={form0781WorkflowChoices.COMPLETE_ONLINE_FORM}
                checked={
                  selectedMentalHealthWorkflowChoice ===
                  form0781WorkflowChoices.COMPLETE_ONLINE_FORM
                }
              />
              <va-radio-option
                label={
                  form0781WorkflowChoiceLabels[
                    form0781WorkflowChoices.SUBMIT_PAPER_FORM
                  ]
                }
                name="private"
                value={form0781WorkflowChoices.SUBMIT_PAPER_FORM}
                checked={
                  selectedMentalHealthWorkflowChoice ===
                  form0781WorkflowChoices.SUBMIT_PAPER_FORM
                }
              />
              <va-radio-option
                label={
                  form0781WorkflowChoiceLabels[
                    form0781WorkflowChoices.OPT_OUT_OF_FORM0781
                  ]
                }
                name="private"
                value={form0781WorkflowChoices.OPT_OUT_OF_FORM0781}
                checked={
                  selectedMentalHealthWorkflowChoice ===
                  form0781WorkflowChoices.OPT_OUT_OF_FORM0781
                }
              />
            </VaRadio>
            <div>
              <p>
                <strong>Note:</strong> If you choose to fill out the PDF
                version, you can download the PDF now.
              </p>
              <p>
                <va-link
                  external
                  href="https://www.va.gov/find-forms/about-form-21-0781/"
                  text="Download VA Form 21-0781 to submit your statement"
                />
              </p>
            </div>
          </div>
          {!onReviewPage ? (
            <>
              {traumaticEventsExamples}
              {mentalHealthSupportAlert()}
            </>
          ) : null}
        </div>
        <VaModal
          clickToClose
          status="warning"
          modalTitle={modalTitle}
          primaryButtonText={primaryText}
          secondaryButtonText={secondaryText}
          onPrimaryButtonClick={handlers.onGoForward}
          onSecondaryButtonClick={handlers.onCloseModal}
          onCloseEvent={handlers.onCloseModal}
          visible={modalVisible}
          uswds
        >
          {modalContent}
        </VaModal>
      </fieldset>
      {onReviewPage ? (
        /**
         * Does not use web component for design consistency on all pages.
         * @see https://va.ghe.com/software/vets-website/pull/35911
         */
        // eslint-disable-next-line @department-of-veterans-affairs/prefer-button-component
        <button
          className="usa-button-primary"
          type="button"
          onClick={event => handlers.onUpdatePage(event)}
        >
          Update page
        </button>
      ) : (
        <>
          {contentBeforeButtons}
          <FormNavButtons
            goBack={handlers.onGoBack}
            goForward={handlers.onSubmit}
            submitToContinue
            useWebComponents
            enableAnalytics
          />
          {contentAfterButtons}
        </>
      )}
    </form>
  );
};

WorkflowChoicePage.propTypes = {
  contentAfterButtons: PropTypes.element,
  contentBeforeButtons: PropTypes.element,
  data: PropTypes.object,
  goBack: PropTypes.func,
  goForward: PropTypes.func,
  setFormData: PropTypes.func,
  updatePage: PropTypes.bool,
  onReviewPage: PropTypes.func,
};

export default WorkflowChoicePage;
